/**
 * The Network view (design tgNetDraw, L28920-28986), ported as it is: the selected
 * facility in the middle, who pays on the left, referral sources on the right, its systems
 * (EHR, Aithora) above, same-city prospects below. A requestAnimationFrame loop drifts each
 * node a little and runs flow dots along the solid links; hover dims the rest and says what
 * the link is; clicking a neighbour building opens it.
 *
 * The graph keeps its working state on the host element (`__nodes`, `__key`, `__builtFor`,
 * `__raf`) exactly as the design does, so the pixel check can pin both renders to the same
 * frame (pixel/pages/sales-targeting.mjs seeds Math.random, takes over
 * requestAnimationFrame and steps a fixed number of frames). Nothing here changes for a
 * person using the page.
 */
import { useEffect, useRef } from 'react';

export interface NetNode {
  id: string;
  l: string;
  cls: 'pay' | 'ref' | 'sys' | 'us' | 'peer';
  w: number;
  d: string;
  dashed?: boolean;
  /** a neighbour building: clicking it selects that facility */
  k?: string;
}

export interface NetModel {
  /** Rebuild the layout when this changes (facility, EHR, deal). */
  key: string;
  name: string;
  tier: string | null;
  nodes: NetNode[];
}

interface LaidNode extends Omit<NetNode, 'cls'> {
  cls: NetNode['cls'] | 'focal';
  x: number;
  y: number;
  r: number;
  ph: number;
  dots: number[];
}

interface Host extends HTMLDivElement {
  __key?: string;
  __net?: NetModel;
  __raf?: number;
  __nodes?: LaidNode[];
  __W?: number;
  __H?: number;
  __builtFor?: string;
  __hover?: string | null;
}

const CL: Record<string, string> = { pay: '#1F6B52', ref: '#2F5C97', sys: '#5B6B7C', us: '#12503C', peer: '#6B5BB8', focal: '#22272E' };

export function NetView({ net, onTip, onPick }: { net: NetModel; onTip: (t: string) => void; onPick: (k: string) => void }) {
  const ref = useRef<Host>(null);
  const cb = useRef({ onTip, onPick });
  cb.current = { onTip, onPick };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.__key === net.key && el.__raf) {
      el.__net = net;
      return;
    }
    el.__key = net.key;
    el.__net = net;
  }, [net]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cv = el.querySelector('canvas');
    if (!cv) {
      cv = document.createElement('canvas');
      cv.style.display = 'block';
      el.appendChild(cv);
    }
    const canvas = cv;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const build = () => {
      const W = el.clientWidth || 600,
        H = el.clientHeight || 400,
        dpr = window.devicePixelRatio || 1;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cx = W / 2,
        cy = H / 2,
        Rx = Math.min(W * 0.38, 330),
        Ry = Math.min(H * 0.36, 230);
      const groups: Record<string, [number, number]> = { pay: [Math.PI * 0.76, Math.PI * 1.22], ref: [-Math.PI * 0.28, Math.PI * 0.26], sys: [Math.PI * 1.4, Math.PI * 1.6], peer: [Math.PI * 0.36, Math.PI * 0.64] };
      const byG: Record<string, NetNode[]> = {};
      el.__net!.nodes.forEach((n) => {
        const g = n.cls === 'us' ? 'sys' : n.cls;
        (byG[g] = byG[g] || []).push(n);
      });
      const nodes: LaidNode[] = [{ id: 'focal', l: el.__net!.name, cls: 'focal', w: 0, d: '', x: cx, y: cy, r: 30, ph: 0, dots: [] }];
      Object.keys(byG).forEach((g) => {
        const L = byG[g],
          a = groups[g] || [0, 1];
        L.forEach((n, i) => {
          const t = L.length === 1 ? 0.5 : i / (L.length - 1);
          const ang = a[0] + (a[1] - a[0]) * t;
          const rm = g === 'peer' ? 0.95 + (i % 2) * 0.22 : 0.86 + (i % 2) * 0.18;
          nodes.push({
            ...n,
            x: cx + Math.cos(ang) * Rx * rm,
            y: cy + Math.sin(ang) * Ry * rm,
            r: 9 + Math.sqrt(n.w) * 1.6,
            ph: Math.random() * 6.28,
            dots: [Math.random(), Math.random(), Math.random()].slice(0, n.dashed ? 0 : Math.max(1, Math.round(n.w / 20))),
          });
        });
      });
      el.__nodes = nodes;
      el.__W = W;
      el.__H = H;
    };
    build();
    el.__builtFor = el.__key;
    const hit = (ev: MouseEvent) => {
      const b = canvas.getBoundingClientRect();
      const x = ev.clientX - b.left,
        y = ev.clientY - b.top;
      return (el.__nodes || []).filter((n) => Math.hypot(n.x - x, n.y - y) <= n.r + 4)[0] || null;
    };
    const move = (ev: MouseEvent) => {
      const n = hit(ev);
      el.__hover = n ? n.id : null;
      canvas.style.cursor = n && n.k ? 'pointer' : 'default';
      const tip = n ? (n.cls === 'focal' ? el.__net!.name + ' · the facility you are working' : n.l + ': ' + n.d) : null;
      if (tip) cb.current.onTip(tip);
    };
    const click = (ev: MouseEvent) => {
      const n = hit(ev);
      if (n && n.k) cb.current.onPick(n.k);
    };
    const resize = () => {
      if (el.isConnected) build();
    };
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('click', click);
    window.addEventListener('resize', resize);
    const q = (a: number, m: number, b: number, t: number) => (1 - t) * (1 - t) * a + 2 * (1 - t) * t * m + t * t * b;
    const frame = () => {
      if (!el.isConnected) {
        el.__raf = 0;
        return;
      }
      if (el.__builtFor !== el.__key) {
        build();
        el.__builtFor = el.__key;
      }
      const W = el.__W!,
        H = el.__H!,
        nodes = el.__nodes!,
        f = nodes[0],
        hv = el.__hover;
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.strokeStyle = 'rgba(34,39,46,.06)';
      ctx.setLineDash([2, 5]);
      [0.55, 0.9, 1.2].forEach((m) => {
        ctx.beginPath();
        ctx.ellipse(f.x, f.y, Math.min(W * 0.38, 330) * m, Math.min(H * 0.36, 230) * m, 0, 0, 6.28);
        ctx.stroke();
      });
      ctx.restore();
      nodes.slice(1).forEach((n) => {
        if (!reduce) {
          n.ph += 0.016;
          n.x += Math.cos(n.ph) * 0.12;
          n.y += Math.sin(n.ph * 1.3) * 0.12;
        }
        const dim = hv && hv !== n.id && hv !== 'focal';
        const mx = (f.x + n.x) / 2,
          my = (f.y + n.y) / 2 - 16;
        ctx.save();
        ctx.globalAlpha = dim ? 0.2 : 1;
        ctx.strokeStyle = CL[n.cls];
        ctx.lineWidth = n.dashed ? 1.3 : 1 + n.w / 14;
        if (n.dashed) ctx.setLineDash([4, 4]);
        ctx.globalAlpha *= n.dashed ? 0.55 : 0.5;
        ctx.beginPath();
        ctx.moveTo(f.x, f.y);
        ctx.quadraticCurveTo(mx, my, n.x, n.y);
        ctx.stroke();
        ctx.restore();
        if (!reduce && !dim)
          n.dots.forEach((p0, i) => {
            let p = p0 + 0.005 + n.w * 0.00012;
            if (p > 1) p -= 1;
            n.dots[i] = p;
            const t = n.cls === 'pay' || n.cls === 'ref' ? 1 - p : p;
            ctx.beginPath();
            ctx.fillStyle = CL[n.cls];
            ctx.arc(q(f.x, mx, n.x, t), q(f.y, my, n.y, t), 2.3, 0, 6.28);
            ctx.fill();
          });
      });
      nodes.forEach((n) => {
        const dim = hv && hv !== n.id && n.cls !== 'focal' && hv !== 'focal';
        ctx.save();
        ctx.globalAlpha = dim ? 0.35 : 1;
        if (n.cls === 'focal' && !reduce) {
          const pr = 1 + Math.sin(Date.now() / 600) * 0.07;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r * pr + 8, 0, 6.28);
          ctx.fillStyle = 'rgba(47,92,151,.12)';
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, 6.28);
        ctx.fillStyle = n.cls === 'focal' ? '#FCFAF4' : n.dashed ? '#FCFAF4' : CL[n.cls];
        ctx.fill();
        ctx.lineWidth = n.cls === 'focal' ? 2.5 : 2;
        ctx.strokeStyle = n.cls === 'focal' ? '#2F5C97' : CL[n.cls];
        if (n.dashed) ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
        if (n.id === hv) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 4, 0, 6.28);
          ctx.strokeStyle = CL[n.cls];
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
        ctx.fillStyle = '#22272E';
        ctx.textAlign = 'center';
        ctx.font = (n.cls === 'focal' ? '800 13px ' : '700 11.5px ') + 'Archivo, sans-serif';
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#F7F3EA';
        ctx.strokeText(n.l, n.x, n.y + n.r + 15);
        ctx.fillText(n.l, n.x, n.y + n.r + 15);
        if (n.cls === 'focal' && el.__net!.tier) {
          ctx.font = '800 15px Archivo, sans-serif';
          ctx.fillStyle = '#2F5C97';
          ctx.fillText(el.__net!.tier, n.x, n.y + 5);
        }
        ctx.restore();
      });
      el.__raf = requestAnimationFrame(frame);
    };
    if (!el.__raf) el.__raf = requestAnimationFrame(frame);
    return () => {
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('click', click);
      window.removeEventListener('resize', resize);
      if (el.__raf) cancelAnimationFrame(el.__raf);
      el.__raf = 0;
    };
  }, []);

  return <div ref={ref} data-testid="tg-net" style={{ position: 'absolute', inset: 0 }} />;
}
